/**
 * Values that vary per deployment of the same build, injected into index.html
 * as `window.configOverrides` (vite/generateHtmlInclude.ts) and substituted by
 * the container at startup (docker-entrypoint.d/90-config.sh) or, for PR
 * builds, a `sed` step in the deploy workflow. The script runs in <head>, so
 * this resolves synchronously - no fetch, no load ordering.
 *
 * Where the app is served from is deliberately absent: the base URL is baked
 * into asset URLs, the PWA manifest and the precache manifest at build time, so
 * it cannot vary at runtime. Refer to the resolved value in app code using BASE_PATH.
 */
export interface RuntimeConfig {
  /** Hydra trading gateway websocket URL. */
  hydraUrl: string
  /** Build/release identifier shown in the UI footer. */
  releaseVersion: string
  /** Pre-fills the Equities login form; empty when none is configured. */
  equitiesDefaultEmail: string
}

/** No config was ever injected/substituted for this deployment. */
const MISSING_HYDRA_URL = "MISSING_HYDRA_URL_CONFIG"

/** No config was ever injected/substituted for this deployment. */
const MISSING_RELEASE_VERSION = "VersionNotSet"

/**
 * e.g. "@HYDRA_URL@" - a placeholder that was never substituted, meaning
 * whatever should have substituted it (container entrypoint, deploy workflow)
 * didn't run. Treated as absent rather than passed through as a broken value.
 */
const isUnsubstituted = (value: string): boolean =>
  /^@[A-Z_][A-Z0-9_]*@$/u.test(value)

/** The trimmed override, or undefined if it is missing or un-substituted. */
const sanitizeOverride = (value: string | undefined): string | undefined => {
  if (typeof value !== "string") return undefined

  const trimmed = value.trim()
  if (trimmed === "" || isUnsubstituted(trimmed)) return undefined

  return trimmed
}

/** Exported for testing - application code should import `runtimeConfig`. */
export const resolveConfig = (
  overrides: Window["configOverrides"],
): RuntimeConfig =>
  Object.freeze({
    // Deliberately not "" - a valid-but-empty URL would fail to connect the
    // same way a config bug would, but with no clue why. This string shows up
    // directly in the failed connection attempt, in logs and in the network
    // tab, so a missing config diagnoses itself instead of looking like a
    // generic connectivity problem.
    hydraUrl: sanitizeOverride(overrides?.hydraUrl) ?? MISSING_HYDRA_URL,
    releaseVersion:
      sanitizeOverride(overrides?.releaseVersion) ?? MISSING_RELEASE_VERSION,
    // No sentinel, unlike the two above: an unset default is a working state (an
    // empty field), so a placeholder would only potentially add a bogus,
    // submittable address.
    equitiesDefaultEmail:
      sanitizeOverride(overrides?.equitiesDefaultEmail) ?? "",
  })

export const runtimeConfig = resolveConfig(window.configOverrides)
